import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { 
  getSubmissions, 
  updateSubmissionStatus, 
  deleteSubmission,
  updateSubmission,
  submitForm
} from '../../services/apiService';

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [user, setUser] = useState(null);
  const [importStatus, setImportStatus] = useState({ message: '', type: '' });
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  
  const navigate = useNavigate();

  // Main useEffect - load user and submissions
  useEffect(() => {
    const savedUser = localStorage.getItem('adminUser');
    const savedToken = localStorage.getItem('adminToken');
    
    if (!savedUser || !savedToken) {
      navigate('/login');
      return;
    }
    
    try {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      fetchSubmissions();
    } catch (error) {
      handleLogout();
    }
  }, [navigate]);

  // Filter submissions when dependencies change
  useEffect(() => {
    filterSubmissions();
  }, [submissions, searchTerm, statusFilter, propertyTypeFilter, priorityFilter, serviceFilter]);

  // Fetch submissions function
  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const data = await getSubmissions();
      const submissionsArray = Array.isArray(data) ? data : [];
      setSubmissions(submissionsArray);
      setError('');
    } catch (err) {
      let errorMessage = err.message || 'Failed to fetch submissions';
      
      if (err.message.includes('401') || err.message.includes('token')) {
        errorMessage = 'Authentication failed. Please login again.';
        handleLogout();
      }
      
      setError(errorMessage);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/login');
  };

  // Excel Import Functionality
  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportStatus({ message: 'Processing Excel file...', type: 'info' });

    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (jsonData.length === 0) {
          setImportStatus({ message: 'No data found in Excel file', type: 'error' });
          return;
        }

        let successCount = 0;
        let errorCount = 0;

        for (const row of jsonData) {
          try {
            const formData = processExcelData(row);
            await submitForm(formData);
            successCount++;
          } catch (err) {
            console.error('Error importing row:', err);
            errorCount++;
          }
        }

        setImportStatus({ 
          message: `Import completed: ${successCount} successful, ${errorCount} failed`, 
          type: successCount > 0 ? 'success' : 'error' 
        });

        if (successCount > 0) {
          setTimeout(() => {
            fetchSubmissions();
          }, 1000);
        }

      } catch (error) {
        setImportStatus({ 
          message: 'Error processing Excel file. Please check the format.', 
          type: 'error' 
        });
      }
    };

    reader.onerror = () => {
      setImportStatus({ 
        message: 'Error reading file', 
        type: 'error' 
      });
    };

    reader.readAsArrayBuffer(file);
    
    // Reset file input and close modal
    e.target.value = '';
    setTimeout(() => {
      setShowImportModal(false);
      setImportStatus({ message: '', type: '' });
    }, 3000);
  };

  const processExcelData = (excelRow) => {
    const cleanQuoteAmount = (amount) => {
      if (!amount) return null;
      const cleaned = String(amount)
        .replace(/[£$€,Â\s]/g, '')
        .trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    return {
      customerName: excelRow['Customer Name'] || excelRow['customerName'] || excelRow['Name'] || '',
      email: excelRow['Email'] || excelRow['email'] || excelRow['Email Address'] || '',
      phoneNumber: excelRow['Phone'] || excelRow['phoneNumber'] || excelRow['Phone Number'] || excelRow['Telephone'] || '',
      postcode: excelRow['Postcode'] || excelRow['postcode'] || excelRow['Zip'] || '',
      address: excelRow['Address'] || excelRow['address'] || excelRow['Street'] || '',
      county: excelRow['County'] || excelRow['county'] || excelRow['State'] || excelRow['Region'] || '',
      location: excelRow['Location'] || excelRow['location'] || excelRow['Town'] || excelRow['City'] || '',
      propertyType: excelRow['Property Type'] || excelRow['propertyType'] || excelRow['Type'] || '',
      approxArea: excelRow['Area'] || excelRow['approxArea'] || excelRow['Square Meters'] || excelRow['SQM'] || '',
      condition: excelRow['Condition'] || excelRow['condition'] || excelRow['Status'] || '',
      preferredStart: excelRow['Start Date'] || excelRow['preferredStart'] || excelRow['Timeline'] || '',
      quoteAmount: cleanQuoteAmount(excelRow['Quote Amount'] || excelRow['quoteAmount'] || excelRow['Budget'] || excelRow['Price']),
      priority: excelRow['Priority'] || excelRow['priority'] || 'medium',
      additionalNotes: excelRow['Notes'] || excelRow['additionalNotes'] || excelRow['Comments'] || '',
      preferredContact: parseArrayField(excelRow['Preferred Contact'] || excelRow['preferredContact']),
      existingSurface: parseArrayField(excelRow['Existing Surface'] || excelRow['existingSurface']),
      serviceRequired: parseArrayField(excelRow['Services Required'] || excelRow['serviceRequired'] || excelRow['Services']),
      leadOrigin: excelRow['Lead Origin'] || excelRow['leadOrigin'] || 'excel-import',
      generatedBy: excelRow['Generated By'] || excelRow['generatedBy'] || 'excel-import'
    };
  };

  const parseArrayField = (fieldValue) => {
    if (!fieldValue) return [];
    if (Array.isArray(fieldValue)) return fieldValue;
    if (typeof fieldValue === 'string') {
      return fieldValue.split(',').map(item => item.trim().toLowerCase());
    }
    return [];
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        'Customer Name': 'John Smith',
        'Email': 'john.smith@example.com',
        'Phone Number': '+44 123 456 7890',
        'Location': 'London',
        'Address': '123 Main Street',
        'Postcode': 'SW1A 1AA',
        'County': 'London',
        'Property Type': 'residential',
        'Area': '50',
        'Condition': 'good',
        'Preferred Start': 'asap',
        'Quote Amount': '5000',
        'Priority': 'medium',
        'Preferred Contact': 'phone,email',
        'Existing Surface': 'concrete,grass',
        'Services Required': 'resin driveway,resin patio',
        'Lead Origin': 'website',
        'Generated By': 'main-website-form',
        'Notes': 'Sample notes'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.writeFile(workbook, 'leads-import-template.xlsx');
  };

  const filterSubmissions = () => {
    let filtered = submissions;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(sub => 
        sub.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.phoneNumber?.includes(searchTerm) ||
        sub.postcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.leadOrigin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.generatedBy?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }

    // Property type filter
    if (propertyTypeFilter !== 'all') {
      filtered = filtered.filter(sub => sub.propertyType === propertyTypeFilter);
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(sub => sub.priority === priorityFilter);
    }

    // Service filter
    if (serviceFilter !== 'all') {
      filtered = filtered.filter(sub => 
        sub.serviceRequired && sub.serviceRequired.includes(serviceFilter)
      );
    }

    setFilteredSubmissions(filtered);
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await updateSubmissionStatus(id, newStatus);
      setSubmissions(prev => prev.map(sub => 
        sub.id === id ? { ...sub, status: newStatus } : sub
      ));
    } catch (err) {
      setError(err.message || 'Failed to update status');
      fetchSubmissions();
    }
  };

  const handleEdit = (submission) => {
    setEditingSubmission({...submission});
  };

const handleSaveEdit = async () => {
  try {
    console.log('🔄 Starting save operation...');
    console.log('📝 Editing submission data:', editingSubmission);
    
    // Create a function to properly clean values
    const cleanValue = (value) => {
      if (value === "null" || value === null || value === undefined) {
        return "";
      }
      return value;
    };

    // Clean all the fields properly
    const cleanedSubmission = {
      ...editingSubmission,
      customerName: cleanValue(editingSubmission.customerName) || "Unknown Customer",
      email: cleanValue(editingSubmission.email),
      phoneNumber: cleanValue(editingSubmission.phoneNumber),
      postcode: cleanValue(editingSubmission.postcode),
      address: cleanValue(editingSubmission.address),
      county: cleanValue(editingSubmission.county),
      location: cleanValue(editingSubmission.location),
      propertyType: cleanValue(editingSubmission.propertyType),
      approxArea: cleanValue(editingSubmission.approxArea),
      condition: cleanValue(editingSubmission.condition),
      preferredStart: cleanValue(editingSubmission.preferredStart),
      leadOrigin: cleanValue(editingSubmission.leadOrigin),
      generatedBy: cleanValue(editingSubmission.generatedBy),
      additionalNotes: cleanValue(editingSubmission.additionalNotes),
      // Handle numeric fields
      quoteAmount: editingSubmission.quoteAmount && editingSubmission.quoteAmount !== "null" 
        ? editingSubmission.quoteAmount 
        : null,
      // Ensure required fields have defaults
      priority: editingSubmission.priority || 'medium',
      status: editingSubmission.status || 'new'
    };

    console.log('📤 Sending PROPERLY CLEANED data to backend:', cleanedSubmission);
    
    const result = await updateSubmission(cleanedSubmission.id, cleanedSubmission);
    console.log('✅ Backend response:', result);
    
    if (result && result.submission) {
      console.log('🎯 Update confirmed in response:', {
        receivedCustomerName: result.submission.customer_name,
        receivedStatus: result.submission.status
      });
    }
    
    // Refresh the data
    setTimeout(() => {
      fetchSubmissions();
    }, 500);
    
    setEditingSubmission(null);
    setError('Submission updated successfully!');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setError('');
    }, 3000);
    
  } catch (err) {
    console.error('❌ Save operation failed:', err);
    setError(err.message || 'Failed to update submission');
  }
};

  const handleDelete = (submission) => {
    setSubmissionToDelete(submission);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteSubmission(submissionToDelete.id);
      setSubmissions(prev => prev.filter(sub => sub.id !== submissionToDelete.id));
      setShowDeleteModal(false);
      setSubmissionToDelete(null);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to delete submission');
    }
  };

  // View submission details
  const viewSubmissionDetails = (submission) => {
    setSelectedSubmission(submission);
  };

  // Export Function
  const exportToExcel = () => {
    const headers = [
      'ID', 'Date', 'Customer Name', 'Email', 'Phone', 'Location', 
      'Postcode', 'Address', 'County', 'Property Type', 'Approx Area', 
      'Existing Surface', 'Condition', 'Services Required', 'Preferred Start', 
      'Quote Amount', 'Priority', 'Preferred Contact', 'Lead Origin', 
      'Generated By', 'Status', 'Additional Notes'
    ];
    
    const csvData = submissions.map(sub => [
      sub.id,
      new Date(sub.created_at).toLocaleDateString(),
      sub.customerName,
      sub.email,
      sub.phoneNumber,
      sub.location,
      sub.postcode,
      sub.address,
      sub.county,
      sub.propertyType,
      sub.approxArea,
      sub.existingSurface?.join(', '),
      sub.condition,
      sub.serviceRequired?.join(', '),
      sub.preferredStart,
      sub.quoteAmount ? `£${sub.quoteAmount}` : '',
      sub.priority,
      sub.preferredContact?.join(', '),
      sub.leadOrigin,
      sub.generatedBy,
      sub.status,
      sub.additionalNotes
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

const formatDate = (dateString) => {
  console.log('📅 Formatting date:', dateString); // Debug log
  
  if (!dateString || dateString === 'null' || dateString === 'undefined') {
    return 'No date';
  }
  
  try {
    // Handle different date formats
    let date;
    
    if (typeof dateString === 'string' && dateString.includes('T')) {
      // ISO format (e.g., "2024-01-01T12:00:00.000Z")
      date = new Date(dateString);
    } else if (typeof dateString === 'string' && dateString.includes('-')) {
      // Date string format (e.g., "2024-01-01")
      date = new Date(dateString + 'T00:00:00');
    } else if (typeof dateString === 'number') {
      // Timestamp
      date = new Date(dateString);
    } else {
      // Fallback - try to parse as is
      date = new Date(dateString);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('❌ Invalid date:', dateString);
      return 'Invalid Date';
    }
    
    // Format as DD/MM/YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('❌ Date formatting error:', error, 'for date:', dateString);
    return 'Date Error';
  }
};

// ADD THIS MISSING FUNCTION:
const getSubmissionDate = (submission) => {
  // Try multiple date fields in order of preference
  const dateSources = [
    submission.created_at,
    submission.createdAt, 
    submission.date,
    submission.updated_at,
    submission.updatedAt
  ];
  
  for (const dateSource of dateSources) {
    if (dateSource && dateSource !== 'null' && dateSource !== 'undefined') {
      return dateSource;
    }
  }
  
  // Fallback: use current date for new submissions
  return new Date().toISOString();
};

  const getUniqueServices = () => {
    const allServices = submissions.flatMap(sub => sub.serviceRequired || []);
    return [...new Set(allServices)].filter(service => service);
  };

  // Conditional formatting functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'contacted': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'quoted': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'won': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'lost': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'good': return 'text-green-600 dark:text-green-400';
      case 'fair': return 'text-yellow-600 dark:text-yellow-400';
      case 'poor': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Redirect to login if no user
  if (!user) {
    return null;
  }

  // Loading state
  if (loading) return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Loading submissions...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="bg-gradient-to-br from-primary-500 to-primary-700 text-white rounded-2xl p-8 mb-8 shadow-xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Lead Management Dashboard</h1>
              <p className="text-white/90">Welcome back, {user?.username}</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button 
                onClick={() => setShowImportModal(true)}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 flex items-center gap-2"
              >
                📥 Import from Excel
              </button>
              <button 
                onClick={exportToExcel}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 flex items-center gap-2 backdrop-blur-sm"
              >
                📊 Export to Excel
              </button>
              <button 
                onClick={fetchSubmissions}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 flex items-center gap-2"
              >
                🔄 Refresh
              </button>
              <button 
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 flex items-center gap-2"
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </header>

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Import Leads from Excel</h2>
              
              {importStatus.message ? (
                <div className={`p-4 rounded-lg mb-4 text-center ${
                  importStatus.type === 'success' 
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                    : importStatus.type === 'error' 
                    ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                }`}>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-lg">
                      {importStatus.type === 'success' ? '✅' : 
                       importStatus.type === 'error' ? '❌' : '⏳'}
                    </span>
                    <span className="font-medium">{importStatus.message}</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    <div className="text-center">
                      <label className="block bg-primary-500 hover:bg-primary-600 text-white px-6 py-4 rounded-lg font-semibold text-lg cursor-pointer transition-all duration-200 transform hover:scale-105 mb-3">
                        📁 Choose Excel File
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={handleFileImport}
                          className="hidden"
                        />
                      </label>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Supports .xlsx, .xls, and .csv files
                      </p>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                        <span>💡</span>
                        Excel Format
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                        Use columns like: <strong>Customer Name, Email, Phone Number, Location, Services Required</strong>
                      </p>
                      <button 
                        onClick={downloadTemplate}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline"
                      >
                        Download Template
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button 
                      onClick={() => setShowImportModal(false)} 
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center gap-4 transition-transform hover:-translate-y-1 shadow-sm">
            <div className="text-3xl text-primary-500">📈</div>
            <div>
              <h3 className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1">{submissions.length}</h3>
              <p className="text-gray-600 dark:text-gray-400 font-medium">Total Leads</p>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center gap-4 transition-transform hover:-translate-y-1 shadow-sm">
            <div className="text-3xl text-green-500">🆕</div>
            <div>
              <h3 className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">
                {submissions.filter(s => s.status === 'new').length}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 font-medium">New Leads</p>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center gap-4 transition-transform hover:-translate-y-1 shadow-sm">
            <div className="text-3xl text-blue-500">💬</div>
            <div>
              <h3 className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                {submissions.filter(s => s.status === 'contacted').length}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 font-medium">Contacted</p>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center gap-4 transition-transform hover:-translate-y-1 shadow-sm">
            <div className="text-3xl text-orange-500">⚠️</div>
            <div>
              <h3 className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-1">
                {submissions.filter(s => s.priority === 'high').length}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 font-medium">High Priority</p>
            </div>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex-1 max-w-2xl">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name, email, phone, location, postcode, lead origin..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 shadow-sm"
              />
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 text-xl">🔍</span>
            </div>
          </div>
          
          <div className="flex gap-2 ml-4">
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                viewMode === 'table' 
                  ? 'bg-primary-500 text-white shadow-md' 
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              📊 Table View
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                viewMode === 'card' 
                  ? 'bg-primary-500 text-white shadow-md' 
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              🃏 Card View
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[140px] cursor-pointer transition-colors shadow-sm text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="quoted">Quoted</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>

          <select 
            value={propertyTypeFilter} 
            onChange={(e) => setPropertyTypeFilter(e.target.value)}
            className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[140px] cursor-pointer transition-colors shadow-sm text-sm"
          >
            <option value="all">All Property Types</option>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>

          <select 
            value={priorityFilter} 
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[140px] cursor-pointer transition-colors shadow-sm text-sm"
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>

          <select 
            value={serviceFilter} 
            onChange={(e) => setServiceFilter(e.target.value)}
            className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[140px] cursor-pointer transition-colors shadow-sm text-sm"
          >
            <option value="all">All Services</option>
            {getUniqueServices().map(service => (
              <option key={service} value={service}>
                {service.charAt(0).toUpperCase() + service.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Results Count */}
        <div className="text-gray-600 dark:text-gray-400 mb-4 font-medium">
          Showing {filteredSubmissions.length} of {submissions.length} leads
        </div>

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Customer</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Contact</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Location</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Services</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Lead Source</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Priority</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Quote</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Status</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(getSubmissionDate(submission))}
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="font-semibold text-gray-900 dark:text-white text-sm">
                          {submission.customerName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {submission.propertyType}
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="text-sm">
                          <div className="text-gray-900 dark:text-white">{submission.phoneNumber}</div>
                          <div className="text-gray-600 dark:text-gray-400 text-xs">{submission.email}</div>
                          {submission.preferredContact && submission.preferredContact.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {submission.preferredContact.map(method => (
                                <span key={method} className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded text-xs font-medium">
                                  {method}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="text-sm">
                          <div className="text-gray-900 dark:text-white">{submission.location}</div>
                          <div className="text-gray-600 dark:text-gray-400 text-xs">{submission.postcode}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex flex-wrap gap-1">
                          {submission.serviceRequired && 
                            (Array.isArray(submission.serviceRequired) 
                              ? submission.serviceRequired.slice(0, 2).map(service => (
                                  <span key={service} className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-xs font-medium">
                                    {service}
                                  </span>
                                ))
                              : (
                                  <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-xs font-medium">
                                    {submission.serviceRequired}
                                  </span>
                                )
                          )}
                          {submission.serviceRequired && 
                            Array.isArray(submission.serviceRequired) && 
                            submission.serviceRequired.length > 2 && (
                              <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded text-xs">
                                +{submission.serviceRequired.length - 2}
                              </span>
                            )}
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="text-sm">
                          <div className="text-gray-900 dark:text-white text-xs font-medium">{submission.leadOrigin}</div>
                          <div className="text-gray-600 dark:text-gray-400 text-xs">by {submission.generatedBy}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(submission.priority)}`}>
                          {submission.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="font-semibold text-primary-600 dark:text-primary-400 text-sm">
                          {submission.quoteAmount ? `£${submission.quoteAmount}` : 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <select 
                          value={submission.status} 
                          onChange={(e) => handleStatusUpdate(submission.id, e.target.value)}
                          className={`text-sm px-2 py-1 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer w-full ${getStatusColor(submission.status)}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="quoted">Quoted</option>
                          <option value="won">Won</option>
                          <option value="lost">Lost</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex gap-1">
                          <button 
                            onClick={() => viewSubmissionDetails(submission)}
                            className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            title="View Details"
                          >
                            👁️
                          </button>
                          <button 
                            onClick={() => handleEdit(submission)}
                            className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => handleDelete(submission)}
                            className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredSubmissions.length === 0 && (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">📭</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No leads found</h3>
                <p className="text-gray-600 dark:text-gray-400">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        )}

        {/* Card View */}
        {viewMode === 'card' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSubmissions.map((submission) => (
              <div key={submission.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-all duration-200">
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1">
                      {submission.customerName}
                    </h3>
                    <div className="flex gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(submission.status)}`}>
                        {submission.status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(submission.priority)}`}>
                        {submission.priority}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(getSubmissionDate(submission))}
                  </div>
                  <div className="font-semibold text-primary-600 dark:text-primary-400 text-lg">
                    {submission.quoteAmount ? `£${submission.quoteAmount}` : 'N/A'}
                  </div>
                </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">📞</span>
                    <span className="text-gray-900 dark:text-white">{submission.phoneNumber}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">✉️</span>
                    <span className="text-gray-900 dark:text-white truncate">{submission.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">📍</span>
                    <span className="text-gray-900 dark:text-white">{submission.location} • {submission.postcode}</span>
                  </div>
                </div>

                {/* Services */}
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Services:</div>
                  <div className="flex flex-wrap gap-1">
                    {submission.serviceRequired && 
                      (Array.isArray(submission.serviceRequired) 
                        ? submission.serviceRequired.slice(0, 3).map(service => (
                            <span key={service} className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-xs font-medium">
                              {service}
                            </span>
                          ))
                        : (
                            <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-xs font-medium">
                              {submission.serviceRequired}
                            </span>
                          )
                    )}
                  </div>
                </div>

                {/* Lead Source */}
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lead Source:</div>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {submission.leadOrigin} • by {submission.generatedBy}
                  </div>
                </div>

                {/* Property Details */}
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Property Type</div>
                    <div className="text-gray-900 dark:text-white font-medium">{submission.propertyType}</div>
                  </div>
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Area</div>
                    <div className="text-gray-900 dark:text-white font-medium">{submission.approxArea || 'N/A'} SQM</div>
                  </div>
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Condition</div>
                    <div className={`font-medium ${getConditionColor(submission.condition)}`}>
                      {submission.condition || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Start</div>
                    <div className="text-gray-900 dark:text-white font-medium">{submission.preferredStart || 'N/A'}</div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button 
                    onClick={() => viewSubmissionDetails(submission)}
                    className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    View Details
                  </button>
                  <button 
                    onClick={() => handleEdit(submission)}
                    className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(submission)}
                    className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Submission Details Modal */}
        {selectedSubmission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Lead Details</h2>
                <button 
                  onClick={() => setSelectedSubmission(null)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Customer Info */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Customer Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Full Name</label>
                        <div className="text-gray-900 dark:text-white font-medium">{selectedSubmission.customerName}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Email</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.email}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Phone</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.phoneNumber}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Preferred Contact</label>
                        <div className="flex gap-1 mt-1">
                          {selectedSubmission.preferredContact?.map(method => (
                            <span key={method} className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs font-medium">
                              {method}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Info */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Location</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Address</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.address}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Location</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.location}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Postcode</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.postcode}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">County</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.county}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Project Details */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Project Details</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Property Type</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.propertyType}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Approximate Area</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.approxArea} SQM</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Existing Surface</label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedSubmission.existingSurface?.map(surface => (
                            <span key={surface} className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-medium">
                              {surface}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Condition</label>
                        <div className={`font-medium ${getConditionColor(selectedSubmission.condition)}`}>
                          {selectedSubmission.condition}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Preferred Start</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.preferredStart}</div>
                      </div>
                    </div>
                  </div>

                  {/* Lead Source & Status */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Lead Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Lead Origin</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.leadOrigin}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Generated By</label>
                        <div className="text-gray-900 dark:text-white">{selectedSubmission.generatedBy}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Priority</label>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(selectedSubmission.priority)}`}>
                          {selectedSubmission.priority}
                        </span>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</label>
                        <select 
                          value={selectedSubmission.status} 
                          onChange={(e) => handleStatusUpdate(selectedSubmission.id, e.target.value)}
                          className={`text-sm px-2 py-1 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer w-full ${getStatusColor(selectedSubmission.status)}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="quoted">Quoted</option>
                          <option value="won">Won</option>
                          <option value="lost">Lost</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Quote Amount</label>
                        <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                          {selectedSubmission.quoteAmount ? `£${selectedSubmission.quoteAmount}` : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Services & Notes */}
              <div className="mt-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Services Required</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedSubmission.serviceRequired && 
                      (Array.isArray(selectedSubmission.serviceRequired) 
                        ? selectedSubmission.serviceRequired.map(service => (
                            <span key={service} className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-3 py-2 rounded-lg text-sm font-medium">
                              {service}
                            </span>
                          ))
                        : (
                            <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-3 py-2 rounded-lg text-sm font-medium">
                              {selectedSubmission.serviceRequired}
                            </span>
                          )
                    )}
                  </div>
                </div>

                {selectedSubmission.additionalNotes && (
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mt-4">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Additional Notes</h3>
                    <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{selectedSubmission.additionalNotes}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button 
                  onClick={() => setSelectedSubmission(null)} 
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
                <button 
                  onClick={() => {
                    setEditingSubmission(selectedSubmission);
                    setSelectedSubmission(null);
                  }}
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Edit Lead
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingSubmission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Edit Lead</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Customer Name</label>
                  <input
                    type="text"
                    value={editingSubmission.customerName}
                    onChange={(e) => setEditingSubmission({...editingSubmission, customerName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={editingSubmission.email}
                    onChange={(e) => setEditingSubmission({...editingSubmission, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="text"
                    value={editingSubmission.phoneNumber}
                    onChange={(e) => setEditingSubmission({...editingSubmission, phoneNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
                  <input
                    type="text"
                    value={editingSubmission.location}
                    onChange={(e) => setEditingSubmission({...editingSubmission, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Lead Origin</label>
                  <input
                    type="text"
                    value={editingSubmission.leadOrigin}
                    onChange={(e) => setEditingSubmission({...editingSubmission, leadOrigin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Generated By</label>
                  <input
                    type="text"
                    value={editingSubmission.generatedBy}
                    onChange={(e) => setEditingSubmission({...editingSubmission, generatedBy: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Quote Amount (£)</label>
                  <input
                    type="number"
                    value={editingSubmission.quoteAmount}
                    onChange={(e) => setEditingSubmission({...editingSubmission, quoteAmount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Priority</label>
                  <select
                    value={editingSubmission.priority}
                    onChange={(e) => setEditingSubmission({...editingSubmission, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                  <select
                    value={editingSubmission.status}
                    onChange={(e) => setEditingSubmission({...editingSubmission, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="quoted">Quoted</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button 
                  onClick={() => setEditingSubmission(null)} 
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSaveEdit}
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Confirm Delete</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Are you sure you want to delete the lead for <strong>{submissionToDelete?.customerName}</strong>?
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowDeleteModal(false)} 
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete Lead
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-500 text-white p-4 rounded-lg mb-6">
            <div className="flex justify-between items-center">
              <span>{error}</span>
              <button onClick={() => setError('')} className="text-white hover:text-red-100">
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;